import moment from 'moment';
import { useState } from 'react';
import RichTextEditor from 'react-rte';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, Avatar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Color } from '../../../../assets/color';
import { api_urls } from '../../../../utils/api-urls';
import RouteHeader from '../../../../components/common/RouteHeader';
import DatatableHeading from '../../../../components/heading/DatatableHeading';
import * as AdminActions from '../../../../redux/actions/adminAction';
import { HideDateFromCurrent } from '../../../../utils/common-function';

const imageFields = [
    { key: "profile_picture", label: "Profile Picture" },
    { key: "business_logo", label: "Business Logo" },
    { key: "report_cover_page", label: "Report Cover Page" },
    { key: "certificate_image", label: "Certificate Image" },
    { key: "company_profile_page_one", label: "Company Profile Page One" },
    { key: "company_profile_page_two", label: "Company Profile Page Two" },
    { key: "company_profile_page_three", label: "Company Profile Page Three" }
];

const AddPartner = ({ mode, label }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const stateData = location?.state?.stateData;
    const { superadminAdminPartnerDetails } = useSelector((state) => state.authReducer);

    const isEditable = (field) => superadminAdminPartnerDetails?.role === 'Admin' && !superadminAdminPartnerDetails?.partner_edit_access?.[field];

    //! Input Start
    const [inputFieldDetail, setInputFieldDetail] = useState({
        //* Personal Info
        registration_number: stateData?.registration_number || '',
        partner_name: stateData?.partner_name || '',
        email: stateData?.email || '',
        mobile_number: stateData?.mobile_number || '',
        company_name: stateData?.company_name || '',
        gender: stateData?.gender || '',
        state: stateData?.state || '',
        city: stateData?.city || '',
        address_one: stateData?.address_one || '',
        address_two: stateData?.address_two || '',

        //* Professional Info
        coverage_area: stateData?.coverage_area || '',
        marks_in_iris_exam: stateData?.marks_in_iris_exam || '',
        total_experience: stateData?.total_experience || '',
        no_of_counselling_done: stateData?.no_of_counselling_done || '',
        rating: stateData?.rating || '',
        counselling_fee: stateData?.counselling_fee || '',
        account_expiry_date: stateData?.account_expiry_date ? moment.utc(stateData.account_expiry_date)?.format('YYYY-MM-DD') : '',
        is_active: stateData?.is_active || false,
        expertise: stateData ? RichTextEditor.createValueFromString(String(stateData?.expertise), 'html') : RichTextEditor.createEmptyValue(),

        //* Images and Documents
        profile_picture: stateData?.profile_picture || '',
        business_logo: stateData?.business_logo || '',
        report_cover_page: stateData?.report_cover_page || '',
        certificate_image: stateData?.certificate_image || '',
        company_profile_page_one: stateData?.company_profile_page_one || '',
        company_profile_page_two: stateData?.company_profile_page_two || '',
        company_profile_page_three: stateData?.company_profile_page_three || '',

        //* Account Info
        password: stateData?.password || '',
        report_wallet_balance: stateData?.report_wallet_balance || '',
        is_community_member: stateData?.is_community_member || false,
    });
    const [inputFieldError, setInputFieldError] = useState({});

    const handleInputFieldDetail = (event) => setInputFieldDetail({ ...inputFieldDetail, [event?.target?.name]: event?.target?.value });  //* Handle Input Field : Data
    const handleInputFieldDetailError = (input, value) => setInputFieldError((prev) => ({ ...prev, [input]: value })); //* Handle Input Field : Error

    const [images, setImages] = useState(() => {
        const initialState = {};
        imageFields.forEach(({ key }) => {
            initialState[key] = {
                file: stateData ? api_urls + stateData[key] : '',
                bytes: ''
            };
        });
        return initialState;
    });

    const handleImageChange = (e, fieldName) => {
        if (e.target.files && e.target.files.length > 0) {
            const fileObj = {
                file: URL.createObjectURL(e.target.files[0]),
                bytes: e.target.files[0]
            };
            setImages(prev => ({
                ...prev,
                [fieldName]: fileObj
            }));
            handleInputFieldDetailError(fieldName, null);
        }
    };
    //! Input End

    //! Handle Submit : Create | Edit
    const handleSubmit = () => {
        console.log({ imageFields, images });

        const {
            registration_number, partner_name, email, mobile_number, company_name, gender, state, city, address_one, address_two,
            coverage_area, marks_in_iris_exam, total_experience, no_of_counselling_done, rating, counselling_fee, account_expiry_date, is_active, expertise,
            password, is_community_member,
        } = inputFieldDetail;

        // Validate counselling fee minimum
        if (counselling_fee && Number(counselling_fee) < 1000) {
            handleInputFieldDetailError("counselling_fee", "Minimum ₹1,000 required");
            return;
        }

        let formData = new FormData();

        formData.append('label', label);
        formData.append('registration_number', registration_number?.trim());
        formData.append('partner_name', partner_name);
        formData.append('email', email);
        formData.append('mobile_number', mobile_number);
        formData.append('company_name', company_name);
        formData.append('gender', gender);
        formData.append('state', state);
        formData.append('city', city);
        formData.append('address_one', address_one);
        formData.append('address_two', address_two);
        formData.append('coverage_area', coverage_area);
        formData.append('marks_in_iris_exam', marks_in_iris_exam);
        formData.append('total_experience', total_experience);
        formData.append('no_of_counselling_done', no_of_counselling_done);
        formData.append('rating', rating);
        formData.append('counselling_fee', counselling_fee);
        formData.append('account_expiry_date', account_expiry_date);
        formData.append('is_active', is_active);
        formData.append('expertise', expertise?.toString('html'));
        formData.append('password', password);
        formData.append('is_community_member', is_community_member);
        formData.append('profile_picture', images['profile_picture']?.bytes);
        formData.append('business_logo', images['business_logo']?.bytes);
        formData.append('report_cover_page', images['report_cover_page']?.bytes);
        formData.append('certificate_image', images['certificate_image']?.bytes);
        formData.append('company_profile_page_one', images['company_profile_page_one']?.bytes);
        formData.append('company_profile_page_two', images['company_profile_page_two']?.bytes);
        formData.append('company_profile_page_three', images['company_profile_page_three']?.bytes);

        if (mode == 'Edit') {
            const payload = {
                id: stateData?._id,
                data: formData,
                onComplete: () => navigate(-1)
            }

            //* Dispatching API For Update
            dispatch(AdminActions.updatePartner(payload));

        } else {
            const payload = {
                data: formData,
                onComplete: () => navigate(-1)
            }

            //!*Dispatching API For Create
            dispatch(AdminActions.createPartner(payload));
        }
    };

    return (
        <>
            <RouteHeader title={`${label}`} sub_title={`Manage your ${label?.toLowerCase()}`} />

            <div style={{ backgroundColor: Color.white, padding: "20px", borderRadius: "10px" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <DatatableHeading title={`${mode} ${label}`} sub_title={`Manage your ${label?.toLowerCase()}`} />
                </div>

                <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                    <Grid item lg={12} sm={12} md={12} xs={12} sx={{ fontWeight: "600" }}>Personal Info</Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Registration Number" variant="outlined" fullWidth disabled={mode == 'Edit'}
                            name="registration_number"
                            value={inputFieldDetail?.registration_number}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.registration_number ? true : false}
                            helperText={inputFieldError.registration_number}
                            onFocus={() => handleInputFieldDetailError("registration_number", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label={`${label} Name`} variant="outlined" fullWidth disabled={isEditable('partner_name')}
                            name="partner_name"
                            value={inputFieldDetail?.partner_name}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.partner_name ? true : false}
                            helperText={inputFieldError.partner_name}
                            onFocus={() => handleInputFieldDetailError("partner_name", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Email" variant="outlined" fullWidth disabled={isEditable('email')}
                            //  disabled={!superadminAdminPartnerDetails?.partner_edit_access?.email && superadminAdminPartnerDetails?.role == 'Admin'}
                            name="email"
                            value={inputFieldDetail?.email}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.email ? true : false}
                            helperText={inputFieldError.email}
                            onFocus={() => handleInputFieldDetailError("email", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Mobile Number" variant="outlined" fullWidth disabled={isEditable('mobile_number')}
                            name="mobile_number"
                            value={inputFieldDetail?.mobile_number}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.mobile_number ? true : false}
                            helperText={inputFieldError.mobile_number}
                            onFocus={() => handleInputFieldDetailError("mobile_number", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Company Name" variant="outlined" fullWidth disabled={isEditable('company_name')}
                            name="company_name"
                            value={inputFieldDetail?.company_name}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.company_name ? true : false}
                            helperText={inputFieldError.company_name}
                            onFocus={() => handleInputFieldDetailError("company_name", null)}
                        />
                    </Grid>

                    <Grid item lg={3} md={12} sm={12} xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Gender</InputLabel>
                            <Select
                                label="Gender" variant="outlined" fullWidth disabled={isEditable('gender')}
                                name='gender'
                                value={inputFieldDetail?.gender}
                                onChange={handleInputFieldDetail}
                                error={inputFieldError?.gender ? true : false}
                                onFocus={() => handleInputFieldDetailError("gender", null)}
                            >
                                <MenuItem disabled>---Select Gender ---</MenuItem>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        {inputFieldError?.gender && <div style={{ color: "#D32F2F", fontSize: "13px", padding: "5px 15px 0 12px", fontWeight: "500" }}>{inputFieldError?.gender}</div>}
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="State" variant="outlined" fullWidth disabled={isEditable('state')}
                            name="state"
                            value={inputFieldDetail?.state}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.state ? true : false}
                            helperText={inputFieldError.state}
                            onFocus={() => handleInputFieldDetailError("state", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="City" variant="outlined" fullWidth disabled={isEditable('city')}
                            name="city"
                            value={inputFieldDetail?.city}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.city ? true : false}
                            helperText={inputFieldError.city}
                            onFocus={() => handleInputFieldDetailError("city", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Address one" variant="outlined" fullWidth disabled={isEditable('address_one')}
                            name="address_one"
                            value={inputFieldDetail?.address_one}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.address_one ? true : false}
                            helperText={inputFieldError.address_one}
                            onFocus={() => handleInputFieldDetailError("address_one", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Address two" variant="outlined" fullWidth disabled={isEditable('address_two')}
                            name="address_two"
                            value={inputFieldDetail?.address_two}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.address_two ? true : false}
                            helperText={inputFieldError.address_two}
                            onFocus={() => handleInputFieldDetailError("address_two", null)}
                        />
                    </Grid>

                    <Grid item lg={12} sm={12} md={12} xs={12} sx={{ fontWeight: "600" }}>Professional Info</Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Coverage area" variant="outlined" fullWidth disabled={isEditable('coverage_area')}
                            name="coverage_area"
                            value={inputFieldDetail?.coverage_area}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.coverage_area ? true : false}
                            helperText={inputFieldError.coverage_area}
                            onFocus={() => handleInputFieldDetailError("coverage_area", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Marks (iris exam)" variant="outlined" fullWidth disabled={isEditable('marks_in_iris_exam')} type='number'
                            name="marks_in_iris_exam"
                            value={inputFieldDetail?.marks_in_iris_exam}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.marks_in_iris_exam ? true : false}
                            helperText={inputFieldError.marks_in_iris_exam}
                            onFocus={() => handleInputFieldDetailError("marks_in_iris_exam", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Total experience" variant="outlined" fullWidth disabled={isEditable('total_experience')} type='number'
                            name="total_experience"
                            value={inputFieldDetail?.total_experience}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.total_experience ? true : false}
                            helperText={inputFieldError.total_experience}
                            onFocus={() => handleInputFieldDetailError("total_experience", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="No.(counselling done)" variant="outlined" fullWidth disabled={isEditable('no_of_counselling_done')} type='number'
                            name="no_of_counselling_done"
                            value={inputFieldDetail?.no_of_counselling_done}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.no_of_counselling_done ? true : false}
                            helperText={inputFieldError.no_of_counselling_done}
                            onFocus={() => handleInputFieldDetailError("no_of_counselling_done", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Rating" variant="outlined" fullWidth disabled={isEditable('rating')} type='number'
                            name="rating"
                            value={inputFieldDetail?.rating}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.rating ? true : false}
                            helperText={inputFieldError.rating}
                            onFocus={() => handleInputFieldDetailError("rating", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Counselling Fee (₹)" variant="outlined" fullWidth disabled={isEditable('counselling_fee')} type="number"
                            name="counselling_fee"
                            value={inputFieldDetail?.counselling_fee}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.counselling_fee ? true : false}
                            helperText={inputFieldError.counselling_fee}
                            onFocus={() => handleInputFieldDetailError("counselling_fee", null)}
                        />
                    </Grid>

                    <Grid item lg={3} sm={12} md={12} xs={12}>
                        <TextField label="Account exp.date" variant="outlined" fullWidth disabled={isEditable('account_expiry_date')} type='date'
                            name="account_expiry_date"
                            value={inputFieldDetail?.account_expiry_date}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.account_expiry_date ? true : false}
                            helperText={inputFieldError.account_expiry_date}
                            onFocus={() => handleInputFieldDetailError("account_expiry_date", null)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: HideDateFromCurrent() }}
                        />
                    </Grid>

                    <Grid item lg={3} md={6} xs={12} >
                        <div style={{ display: "flex", gap: "20px", border: "1px solid #C4C4C4", padding: "15px 13.5px", borderRadius: "5px" }}>
                            <label style={{ cursor: "pointer", color: "#666666", fontSize: "16px", color: "#667284" }} htmlFor="is_active">Active</label>
                            <input disabled={isEditable('is_active')} defaultChecked={inputFieldDetail?.is_active} name='is_active' onClick={(event) => handleInputFieldDetail({ target: { name: 'is_active', value: event?.target?.checked } })} style={{ cursor: "pointer", marginTop: "0px", padding: "0px" }} id='is_active' type="checkbox" />
                        </div>
                    </Grid>

                    <Grid item lg={12} sm={12} md={12} xs={12} sx={{ fontWeight: "600" }}>Expertise</Grid>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <RichTextEditor
                            value={inputFieldDetail?.expertise} disabled={isEditable('expertise')}
                            onChange={value => handleInputFieldDetail({ target: { name: 'expertise', value: value } })}
                            editorStyle={{ minHeight: '50vh', }}
                            onFocus={() => handleInputFieldDetailError("expertise", null)}
                        />
                        {inputFieldError?.expertise && <div style={{ color: "#D32F2F", fontSize: "13px", padding: "5px 15px 0 12px", fontWeight: "400" }}>{inputFieldError?.expertise}</div>}
                    </Grid>

                    <Grid item lg={12} sm={12} md={12} xs={12} sx={{ fontWeight: "600" }}>Images and Documents</Grid>

                    {imageFields.map(({ key, label }) => (
                        <Grid key={key} item lg={4} sm={12} md={12} xs={12}>
                            <div style={{ color: "#000", border: "1px solid #C4C4C4", borderRadius: "3px", padding: '2px 20px' }}>
                                <Grid container spacing={2} sx={{ alignItems: "center" }}>
                                    <Grid item xs={3}>
                                        <Avatar src={images[key]?.file} style={{ width: 40, height: 40 }} />
                                    </Grid>
                                    <Grid item xs={9}>
                                        <label htmlFor={`upload-${key}`} style={{ display: "flex", flexDirection: "column", gap: "3px", padding: "10px 0", cursor: isEditable(key) ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
                                            <p style={{ lineClamp: 1, WebkitLineClamp: 1 }} className='line-clamp-1'>{label}</p>
                                            <p style={{ fontWeight: "400", fontSize: "10px", color: 'green' }}>only png, jpg or jpeg files are allowed</p>
                                            <input disabled={isEditable(key)} id={`upload-${key}`} type="file" accept=".jpg,.jpeg,.png" hidden onChange={(e) => handleImageChange(e, key)} />
                                        </label>
                                    </Grid>
                                </Grid>
                            </div>
                        </Grid>
                    ))}

                    <Grid item lg={12} sm={12} md={12} xs={12} sx={{ fontWeight: "600" }}>Account Info</Grid>

                    <Grid item lg={4} sm={12} md={12} xs={12}>
                        <TextField label="Password" variant="outlined" fullWidth disabled={isEditable('password')}
                            name="password"
                            value={inputFieldDetail?.password}
                            onChange={handleInputFieldDetail}
                            error={inputFieldError.password ? true : false}
                            helperText={inputFieldError.password}
                            onFocus={() => handleInputFieldDetailError("password", null)}
                        />
                    </Grid>

                    {/* <Grid item lg={4} sm={12} md={12} xs={12}>
                        <TextField label="Report/token" variant="outlined" fullWidth disabled
                            name="report_wallet_balance"
                            value={inputFieldDetail?.report_wallet_balance}
                        />
                    </Grid> */}

                    <Grid item lg={4} md={6} xs={12} >
                        <div style={{ display: "flex", gap: "20px", border: "1px solid #C4C4C4", padding: "15px 13.5px", borderRadius: "5px" }}>
                            <label style={{ cursor: "pointer", color: "#666666", fontSize: "16px", color: "#667284" }} htmlFor="is_community_member">Community Member</label>
                            <input disabled={isEditable('is_community_member')} defaultChecked={inputFieldDetail?.is_community_member} name='is_community_member' onClick={(event) => handleInputFieldDetail({ target: { name: 'is_community_member', value: event?.target?.checked } })} style={{ cursor: "pointer", marginTop: "0px", padding: "0px" }} id='is_community_member' type="checkbox" />
                        </div>
                    </Grid>

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container spacing={2} justifyContent="flex-end">
                            <Grid item>
                                <div onClick={() => navigate(-1)} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', backgroundColor: Color.white, color: Color.primary, fontSize: '14px', cursor: 'pointer' }}>Cancel</div>
                            </Grid>

                            <Grid item>
                                <div onClick={handleSubmit} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color.white, fontSize: '14px', cursor: 'pointer' }}>{mode} {label}</div>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </div>
        </>
    )
}

export default AddPartner;